import React from "react";
import { BiSolidOffer } from "react-icons/bi";
import { BsBarChartLine, BsLayersFill } from "react-icons/bs";
import { CiGlobe, CiLogout, CiSettings } from "react-icons/ci";
import { FaExclamation, FaUsers } from "react-icons/fa";
import { IoIosArrowDown, IoIosHelpCircleOutline, IoMdClose } from "react-icons/io";
import { LuWalletCards } from "react-icons/lu";
import { MdDiscount, MdOutlineDashboard, MdOutlineFeedback, MdOutlineProductionQuantityLimits } from "react-icons/md";
import { RiAdminFill, RiNotification2Line, RiUserShared2Fill } from "react-icons/ri";
import { VscRequestChanges } from "react-icons/vsc";
import { twMerge } from "tailwind-merge";
import swyveryLogo from '../../assets/swyvery-logo.png';
import useSidebar from '../../hooks/logics/sidebar/use-sidebar';
import Button from "../Button";
import SidebarLink from "./SidebarLink";
import { useAppSelector } from '../../Redux/store';
import { formatText } from '../../utils/helpers';

const Chevron = ({ open }: { open: boolean }) => (
  <IoIosArrowDown className={twMerge("transition-transform duration-300", open && "rotate-180")} />
);

const SectionButton = ({
  onClick,
  isOpen,
  icon: Icon,
  label,
  isActive = false
}: {
  onClick: () => void;
  isOpen: boolean;
  icon: React.ElementType;
  label: string;
  isActive?: boolean;
}) => (
  <button
    onClick={onClick}
    className={twMerge(
      "group mb-3 flex w-full items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200",
      isActive
        ? "bg-primary text-white shadow-md"
        : "text-gray-700 hover:bg-gray-50"
    )}
  >
    <span className="flex items-center gap-3 flex-1 min-w-0">
      <Icon size={20} className={twMerge(isActive ? "text-white" : "text-primary")} />
      <span className="font-medium text-sm">{label}</span>
    </span>
    <Chevron open={isOpen} />
  </button>
);

const Sidebar = () => {

  const user = useAppSelector((state) => state.auth.user);

  const {
    sectionsOpen,
    toggleSection,
    isSidebarCollapsed,
    isDesktop,
    helpDeskCount,
    unReviewedDriverCount,
    handleLogout,
    handleSidebarToggle,
  } = useSidebar();




  const renderSubSection = (isOpen: boolean, items: React.ReactNode[]) => (
    <div className={twMerge(
      "overflow-hidden transition-all duration-300 ease-in-out space-y-1",
      isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0",
      !isDesktop && !isSidebarCollapsed ? "mr-0" : ""
    )}>
      {items.map((item, i) => (
        <div
          key={i}
          style={{ transitionDelay: `${i * 50}ms` }}
          className={twMerge(
            "transform transition-all duration-300 pl-4",
            isOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
          )}
        >
          {item}
        </div>
      ))}
    </div>
  );

  const Badge = ({ count }: { count: number }) =>
    count > 0 ? (
      <span className="bg-green-100 text-primary text-xs font-semibold px-2 py-1 rounded-full">
        {count}
      </span>
    ) : null;

  return (
    <div className={twMerge(
      "font-inter flex flex-col sticky bg-white top-0 left-0 h-screen w-72 xl:w-[22%] flex-shrink-0 border-r border-gray-200 overflow-hidden",
      isSidebarCollapsed && "hidden",
      !isDesktop && !isSidebarCollapsed ? "w-full fixed z-50 h-screen max-h-screen" : ""
    )}>
      <div className="flex flex-col h-full w-full">

        <div className="px-4 py-4 border-b border-gray-200 flex-shrink-0">
          <div className='flex items-start justify-between'>
            <div className="flex items-center gap-2 mb-4">
              <img src={swyveryLogo} alt="Swyvery" className="w-8 h-8 object-contain" />
              <p className="font-inter text-lg font-semibold text-gray-800">SWYVERY</p>
            </div>
            <div className="flex items-center cursor-pointer md:hidden">
              <IoMdClose onClick={handleSidebarToggle} size={20} />
            </div>

          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <p className="font-semibold text-sm text-gray-800">{user?.firstName} {user?.lastName}</p>
            <p className="text-xs text-gray-600 mb-2">{user?.username}</p>
            <div className="inline-flex items-center bg-green-100 text-primary px-2 py-1 rounded text-xs font-semibold">
              {formatText(user?.userType!)}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-3 pr-2">

          <div>
            <SectionButton
              onClick={() => toggleSection("main")}
              isOpen={sectionsOpen.main}
              icon={MdOutlineDashboard}
              label="Main"
            />
            {renderSubSection(sectionsOpen.main, [
              <SidebarLink icon={MdOutlineDashboard} label="Dashboard" link="/dashboard" onClose={handleSidebarToggle} />,
              <SidebarLink icon={BsBarChartLine} label="Analytics" link="/dashboard/analytics" onClose={handleSidebarToggle} />,
            ])}
          </div>

          <div>
            <SectionButton
              onClick={() => toggleSection("usersAccounts")}
              isOpen={sectionsOpen.usersAccounts}
              icon={FaUsers}
              label="User accounts"
              isActive={sectionsOpen.usersAccounts}
            />
            {renderSubSection(sectionsOpen.usersAccounts, [
              <SidebarLink
                icon={FaUsers}
                label="Users"
                link="/dashboard/users"
                onClose={handleSidebarToggle}
              />,
              <SidebarLink icon={FaUsers} label="Business" link="/dashboard/business" onClose={handleSidebarToggle} />,
              <SidebarLink
                icon={RiUserShared2Fill}
                label="Drivers"
                link="/dashboard/drivers"
                onClose={handleSidebarToggle}
                renderRight={<Badge count={unReviewedDriverCount} />}
              />,
            ])}
          </div>

          <div>
            <SectionButton
              onClick={() => toggleSection("operations")}
              isOpen={sectionsOpen.operations}
              icon={MdOutlineProductionQuantityLimits}
              label="Operations"
            />
            {renderSubSection(sectionsOpen.operations, [
              <SidebarLink icon={MdOutlineProductionQuantityLimits} label="Orders" link="/dashboard/orders" onClose={handleSidebarToggle} />,
              <SidebarLink icon={BsLayersFill} label="Batches" link="/dashboard/batches" onClose={handleSidebarToggle} />,
              <SidebarLink icon={VscRequestChanges} label="Driver documentation update requests" link="/dashboard/driver-documentation-update-requests" onClose={handleSidebarToggle} />,
              <SidebarLink icon={MdOutlineFeedback} label="Feedback" link="/dashboard/feedback" onClose={handleSidebarToggle} />,
              <SidebarLink icon={RiNotification2Line} label="Announcements" link="/dashboard/announcements" onClose={handleSidebarToggle} />,
            ])}
          </div>

          <div>
            <SectionButton
              onClick={() => toggleSection("support")}
              isOpen={sectionsOpen.support}
              icon={FaExclamation}
              label="Support"
            />
            {renderSubSection(sectionsOpen.support, [
              <SidebarLink
                icon={IoIosHelpCircleOutline}
                label="Help desk"
                link="/dashboard/help-desk"
                onClose={handleSidebarToggle}
                renderRight={<Badge count={helpDeskCount} />}
              />,
              <SidebarLink icon={CiGlobe} label="States" link="/dashboard/states" onClose={handleSidebarToggle} />,
            ])}
          </div>

          <div>
            <SectionButton
              onClick={() => toggleSection("management")}
              isOpen={sectionsOpen.management}
              icon={RiAdminFill}
              label="Management"
            />
            {renderSubSection(sectionsOpen.management, [
              <SidebarLink icon={RiAdminFill} label="Admins" link="/dashboard/admins" onClose={handleSidebarToggle} />,
              <SidebarLink icon={RiAdminFill} label="Batch configs" link="/dashboard/batch-configs" onClose={handleSidebarToggle} />,
              <SidebarLink icon={MdDiscount} label="Promo codes" link="/dashboard/promo-codes" onClose={handleSidebarToggle} />,
              <SidebarLink icon={LuWalletCards} label="Subscription plans" link="/dashboard/subscription-plans" onClose={handleSidebarToggle} />,
              <SidebarLink icon={BiSolidOffer} label="Offers" link="/dashboard/offers" onClose={handleSidebarToggle} />,
              <SidebarLink icon={CiSettings} label="Settings" link="/dashboard/accountSettings" onClose={handleSidebarToggle} />,
            ])}
          </div>

        </div>

        <div className="px-3 py-3 border-t border-gray-200 flex-shrink-0">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full text-red-500 px-0 border-none justify-start "
          >
            <CiLogout size={20} className="text-red-500" />
            <span className="ml-2">Logout</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;